import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { parseInput } from './utils';

const AddNodeInline = ({ visible, onCancel, onCreate, variant = 'hud', existingKeys = [] }) => {
  const [name, setName] = useState('');
  const [mode, setMode] = useState('object');
  const [primitiveValue, setPrimitiveValue] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (visible) {
      setName('');
      setMode('object');
      setPrimitiveValue('');
      setError('');
      const id = requestAnimationFrame(() => inputRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
    return undefined;
  }, [visible]);

  const keyExists = useMemo(() => existingKeys?.map((k) => k.toLowerCase()) || [], [existingKeys]);

  const reset = () => {
    setName('');
    setMode('object');
    setPrimitiveValue('');
    setError('');
  };

  const handleCancel = () => {
    reset();
    onCancel?.();
  };

  const handleSubmit = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Name is required');
      return;
    }
    if (keyExists.includes(trimmed.toLowerCase())) {
      setError('Node already exists');
      return;
    }
    const trimmedValue = primitiveValue.trim();
    const value = mode === 'primitive' ? (trimmedValue ? parseInput(undefined, primitiveValue) : null) : mode === 'array' ? [] : {};
    const success = onCreate ? onCreate({ name: trimmed, value, mode }) : true;
    if (success === false) {
      setError('Unable to create node');
      return;
    }
    reset();
    onCancel?.();
  };

  if (!visible) return null;

  const isHud = variant === 'hud';
  const containerStyles = isHud
    ? {
        border: '1px solid rgba(0,255,170,0.25)',
        backgroundColor: 'rgba(6,21,29,0.9)',
        color: '#e9fdf9'
      }
    : {
        border: (theme) => `1px solid ${theme.palette.divider}`,
        backgroundColor: (theme) => theme.palette.background.paper,
        color: '#0f1e24'
      };

  return (
    <Box sx={{ mt: 1, borderRadius: 1.5, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, ...containerStyles }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          inputRef={inputRef}
          label="Node name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setError('');
          }}
          size="small"
          fullWidth
          InputLabelProps={{
            sx: {
              color: isHud ? '#c8fffb' : 'inherit',
              '&.Mui-focused': { color: isHud ? '#ffffff' : 'inherit' }
            }
          }}
          InputProps={{
            sx: {
              color: isHud ? '#ffffff' : 'inherit'
            }
          }}
          sx={
            isHud
              ? {
                  '& .MuiOutlinedInput-root': {
                    height: 32,
                    alignItems: 'center',
                    display: 'flex',
                    borderRadius: 14,
                    backgroundColor: 'rgba(9, 30, 38, 0.92)',
                    boxShadow: 'inset 0 0 0 1px rgba(0,255,170,0.12)',
                    '& input': {
                      padding: '4px 12px',
                      margin: 0,
                      color: '#e7fbf8',
                      lineHeight: 1.2
                    },
                    '& fieldset': {
                      borderRadius: 14,
                      borderColor: 'rgba(0,255,170,0.28)'
                    },
                    '&:hover fieldset': {
                      borderColor: '#00ffaa'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#00ffaa',
                      borderWidth: 1.5
                    }
                  }
                }
              : undefined
          }
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSubmit();
            if (e.key === 'Escape') handleCancel();
          }}
        />
        <ToggleButtonGroup
          exclusive
          value={mode}
          onChange={(_, next) => {
            if (!next) return;
            setMode(next);
            if (next !== 'primitive') {
              setPrimitiveValue('');
            }
          }}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              width: '80px',
              px: 1.5,
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: variant === 'hud' ? 'rgba(0,255,170,0.3)' : 'rgba(0,0,0,0.25)',
              color: variant === 'hud' ? '#d1fffa' : 'inherit'
            },
            '& .MuiToggleButton-root.Mui-selected': {
              borderColor: variant === 'hud' ? '#00ffaa' : 'rgba(0,0,0,0.45)',
              backgroundColor: variant === 'hud' ? 'rgba(0,255,170,0.18)' : 'rgba(0,0,0,0.08)',
              color: '#ffffff'
            }
          }}>
          <ToggleButton value="object">Object</ToggleButton>
          <ToggleButton value="array">Array</ToggleButton>
          <ToggleButton value="primitive">Primitive</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" sx={{ color: variant === 'hud' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>
          {mode === 'object'
            ? 'Creates an empty object you can populate immediately.'
            : mode === 'array'
            ? 'Creates an empty array you can populate immediately.'
            : 'Creates a primitive value (leave blank for a null placeholder).'}
        </Typography>
        <Button
          size="small"
          onClick={handleSubmit}
          variant="outlined"
          sx={{
            textTransform: 'uppercase',
            fontWeight: 600,
            letterSpacing: 0.4,
            px: 1.75,
            border: '1px solid rgba(0,255,170,0.35)',
            color: '#00ffaa',
            backdropFilter: 'blur(2px)'
          }}>
          Create
        </Button>
      </Box>
      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
    </Box>
  );
};

AddNodeInline.propTypes = {
  visible: PropTypes.bool,
  onCancel: PropTypes.func,
  onCreate: PropTypes.func,
  variant: PropTypes.oneOf(['hud', 'page']),
  existingKeys: PropTypes.array
};

export default AddNodeInline;
